import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box, Typography, Card, CardContent, Button, TextField,
  Tab, Tabs, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, CircularProgress, Tooltip, Divider, InputAdornment,
  Avatar, Stack, Collapse,
} from "@mui/material";
import {
  RecordVoiceOver as RecordVoiceOverIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Verified as VerifiedIcon,
  PhoneInTalk as PhoneInTalkIcon,
  CheckCircle as CheckCircleIcon,
  Storage as StorageIcon,
  Tune as TuneIcon,
  ContentCopy as ContentCopyIcon,
  Public as PublicIcon,
  ShieldOutlined as ShieldIcon,
  AutoAwesome as AutoAwesomeIcon,
  PersonAdd as PersonAddIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

import { SCENARIOS, CUSTOM_SCENARIO_OPTION } from "../data/scenarios";
import { generateCallPdf } from "../utils/callPdf";
import BulkCallPanel from "./BulkCallPanel";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

// ── Design tokens ────────────────────────────────────────────────────────
const TOKENS = {
  pageBg: "#f8fafc",
  cardBg: "#ffffff",
  border: "#e5e7eb",
  borderHover: "#cbd5e1",
  text: "#0f172a",
  textMuted: "#64748b",
  textFaint: "#94a3b8",
  primary: "#4f46e5",
  primaryDark: "#4338ca",
  primarySoft: "#eef2ff",
  success: "#059669",
  successSoft: "#ecfdf5",
  warning: "#d97706",
  warningSoft: "#fffbeb",
  danger: "#dc2626",
  dangerSoft: "#fef2f2",
  info: "#0284c7",
  infoSoft: "#f0f9ff",
  accent: "#7c3aed",
};

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Status pill ──────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const map = {
    initiated: { label: "Initiated", fg: TOKENS.textMuted, bg: "#f1f5f9", dot: TOKENS.textFaint },
    ringing: { label: "Ringing", fg: TOKENS.warning, bg: TOKENS.warningSoft, dot: TOKENS.warning },
    "in-progress": { label: "Active", fg: TOKENS.info, bg: TOKENS.infoSoft, dot: TOKENS.info },
    completed: { label: "Completed", fg: TOKENS.success, bg: TOKENS.successSoft, dot: TOKENS.success },
    failed: { label: "Failed", fg: TOKENS.danger, bg: TOKENS.dangerSoft, dot: TOKENS.danger },
  };
  const s = map[status] || map.initiated;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.4,
      background: s.bg, color: s.fg, borderRadius: "6px",
      fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.01em",
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />
      {s.label}
    </Box>
  );
}

// ── KPI card ─────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, accent = TOKENS.primary, accentBg = TOKENS.primarySoft }) {
  return (
    <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "10px" }}>
      <CardContent sx={{ p: "20px !important" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ color: TOKENS.textMuted, fontSize: "0.78rem", fontWeight: 600, letterSpacing: "0.02em", textTransform: "uppercase" }}>
            {label}
          </Typography>
          <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: accentBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon sx={{ fontSize: 18, color: accent }} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: "1.85rem", fontWeight: 700, color: TOKENS.text, lineHeight: 1, letterSpacing: "-0.025em" }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ── Voice avatar (initial-based, gender-tinted) ─────────────────────────
function VoiceAvatar({ name, gender, size = 36 }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const palette = gender === "female"
    ? { bg: "#fef3c7", fg: "#b45309" }
    : gender === "male"
      ? { bg: "#dbeafe", fg: "#1d4ed8" }
      : { bg: TOKENS.primarySoft, fg: TOKENS.primary };
  return (
    <Avatar sx={{
      width: size, height: size, background: palette.bg, color: palette.fg,
      fontSize: size * 0.4, fontWeight: 700, border: `1px solid ${TOKENS.border}`,
    }}>
      {initial}
    </Avatar>
  );
}

// ── Voice library row ────────────────────────────────────────────────────
function VoiceRow({ voice, alreadySaved, saving, onSave }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.5, p: 1.5,
      borderRadius: "8px", border: `1px solid ${alreadySaved ? "#bbf7d0" : TOKENS.border}`,
      background: alreadySaved ? TOKENS.successSoft : "#fff",
      transition: "all 0.15s ease",
      "&:hover": { borderColor: alreadySaved ? "#86efac" : TOKENS.borderHover, background: alreadySaved ? "#d1fae5" : "#fafafa" },
    }}>
      <VoiceAvatar name={voice.name} gender={voice.gender} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: TOKENS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {voice.name.split(" - ")[0].split(" – ")[0]}
          </Typography>
          {alreadySaved && <VerifiedIcon sx={{ fontSize: 14, color: TOKENS.success }} />}
        </Box>
        <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textMuted, mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {voice.description || [voice.gender, voice.accent].filter(Boolean).join(" · ") || "Voice"}
        </Typography>
      </Box>
      <Button
        size="small"
        variant={alreadySaved ? "text" : "outlined"}
        disabled={alreadySaved || saving}
        onClick={() => onSave(voice)}
        sx={{
          minWidth: 64, fontSize: "0.72rem", textTransform: "none", fontWeight: 600,
          borderRadius: "6px",
          ...(alreadySaved
            ? { color: TOKENS.success }
            : { borderColor: TOKENS.border, color: TOKENS.text, "&:hover": { borderColor: TOKENS.primary, color: TOKENS.primary, background: TOKENS.primarySoft } }),
        }}
      >
        {saving ? <CircularProgress size={12} /> : alreadySaved ? "Added" : "Add"}
      </Button>
    </Box>
  );
}

// ── Saved voice card (your library) ──────────────────────────────────────
function SavedVoiceCard({ clone, onDelete }) {
  return (
    <Card sx={{
      border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "10px",
      transition: "all 0.15s ease",
      "&:hover": { borderColor: TOKENS.borderHover, transform: "translateY(-1px)", boxShadow: "0 2px 8px rgba(15,23,42,0.04)" },
    }}>
      <CardContent sx={{ p: "16px !important" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
          <VoiceAvatar name={clone.voiceName} size={40} />
          <Tooltip title="Remove voice">
            <IconButton size="small" onClick={() => onDelete(clone.id)} sx={{ color: TOKENS.textFaint, "&:hover": { color: TOKENS.danger, background: TOKENS.dangerSoft } }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: TOKENS.text, mb: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {clone.voiceName}
        </Typography>
        {clone.description && (
          <Typography sx={{ fontSize: "0.75rem", color: TOKENS.textMuted, mb: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {clone.description}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pt: 1, borderTop: `1px solid ${TOKENS.border}`, mt: 1.5 }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: TOKENS.success }} />
          <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textMuted, fontWeight: 500 }}>Ready to use</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────
function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
      <Box sx={{ width: 56, height: 56, borderRadius: "12px", background: TOKENS.primarySoft, display: "inline-flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
        <Icon sx={{ fontSize: 28, color: TOKENS.primary }} />
      </Box>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: TOKENS.text, mb: 0.5 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: "0.82rem", color: TOKENS.textMuted, maxWidth: 380, mx: "auto", mb: action ? 2.5 : 0 }}>{subtitle}</Typography>}
      {action}
    </Box>
  );
}

// ── Captured Data (rich renderer: outcome / summary / collected / criteria / regex) ─
function CapturedDataView({ data, onCopy }) {
  if (!data || typeof data !== "object" || Object.keys(data).length === 0) {
    return (
      <Typography sx={{ fontSize: "0.78rem", color: TOKENS.textFaint, fontStyle: "italic" }}>
        No data captured yet.
      </Typography>
    );
  }

  const isNewFormat =
    "collected" in data || "criteria" in data || "regex_matches" in data ||
    "outcome" in data || "summary" in data;

  // ── Legacy flat format (old regex-only rows) ──
  if (!isNewFormat) {
    return (
      <Stack spacing={1}>
        {Object.entries(data).map(([k, v]) => (
          <Box key={k} sx={{ background: "#fff", border: `1px solid #bbf7d0`, borderRadius: "8px", p: 1.5 }}>
            <Typography sx={{ fontSize: "0.65rem", color: TOKENS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>{k}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography sx={{ fontWeight: 700, color: TOKENS.text, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis" }}>{String(v)}</Typography>
              <Tooltip title="Copy">
                <IconButton size="small" onClick={() => onCopy(String(v))} sx={{ color: TOKENS.textFaint, "&:hover": { color: TOKENS.primary } }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  const { outcome, summary, collected = {}, criteria = {}, regex_matches: regex = {} } = data;
  const hasAny =
    !!outcome || !!summary ||
    Object.keys(collected).length > 0 ||
    Object.keys(criteria).length > 0 ||
    Object.keys(regex).length > 0;

  if (!hasAny) {
    return (
      <Box>
        <Typography sx={{ fontSize: "0.78rem", color: TOKENS.textFaint, fontStyle: "italic", mb: 1 }}>
          No data captured for this call.
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textFaint, lineHeight: 1.5 }}>
          Tip: configure Data Collection fields on your ElevenLabs agent so the LLM extracts structured outcomes from each call.
        </Typography>
      </Box>
    );
  }

  const outcomeColors = {
    success: { bg: "#dcfce7", border: "#86efac", text: "#166534", label: "Success" },
    failure: { bg: "#fee2e2", border: "#fca5a5", text: "#991b1b", label: "Failure" },
    unknown: { bg: "#f1f5f9", border: "#cbd5e1", text: "#475569", label: "Unknown" },
  };
  const outcomeStyle = outcome ? outcomeColors[String(outcome).toLowerCase()] || outcomeColors.unknown : null;

  const criteriaColors = {
    success: { color: TOKENS.success, bg: "#ecfdf5", label: "Pass" },
    failure: { color: "#dc2626", bg: "#fef2f2", label: "Fail" },
    unknown: { color: TOKENS.textMuted, bg: "#f8fafc", label: "Unknown" },
  };

  const formatKey = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Stack spacing={1.5}>
      {outcomeStyle && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Outcome</Typography>
          <Chip
            label={outcomeStyle.label}
            size="small"
            sx={{ background: outcomeStyle.bg, color: outcomeStyle.text, border: `1px solid ${outcomeStyle.border}`, fontWeight: 700, fontSize: "0.7rem", height: 22 }}
          />
        </Box>
      )}

      {summary && (
        <Box sx={{ background: "#fff", border: `1px solid ${TOKENS.border}`, borderRadius: "8px", p: 1.5 }}>
          <Typography sx={{ fontSize: "0.65rem", color: TOKENS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>Call Summary</Typography>
          <Typography sx={{ fontSize: "0.82rem", color: TOKENS.text, lineHeight: 1.55 }}>{summary}</Typography>
        </Box>
      )}

      {Object.keys(collected).length > 0 && (
        <Box>
          <Typography sx={{ fontSize: "0.65rem", color: TOKENS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>Collected Fields</Typography>
          <Stack spacing={0.75}>
            {Object.entries(collected).map(([k, entry]) => {
              const value = entry && typeof entry === "object" && "value" in entry ? entry.value : entry;
              const rationale = entry && typeof entry === "object" ? entry.rationale : "";
              const displayValue = typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
              return (
                <Box key={k} sx={{ background: "#fff", border: `1px solid #bbf7d0`, borderRadius: "8px", p: 1.5 }}>
                  <Typography sx={{ fontSize: "0.68rem", color: TOKENS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>{formatKey(k)}</Typography>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: TOKENS.text, fontSize: "0.85rem", flex: 1, wordBreak: "break-word" }}>{displayValue}</Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => onCopy(displayValue)} sx={{ color: TOKENS.textFaint, "&:hover": { color: TOKENS.primary } }}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {rationale && (
                    <Typography sx={{ fontSize: "0.72rem", color: TOKENS.textMuted, mt: 0.5, lineHeight: 1.4, fontStyle: "italic" }}>{rationale}</Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {Object.keys(criteria).length > 0 && (
        <Box>
          <Typography sx={{ fontSize: "0.65rem", color: TOKENS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>Evaluation Criteria</Typography>
          <Stack spacing={0.5}>
            {Object.entries(criteria).map(([k, entry]) => {
              const result = entry && typeof entry === "object" && "value" in entry ? entry.value : entry;
              const rationale = entry && typeof entry === "object" ? entry.rationale : "";
              const style = criteriaColors[String(result).toLowerCase()] || criteriaColors.unknown;
              return (
                <Box key={k} sx={{ background: style.bg, border: `1px solid ${TOKENS.border}`, borderRadius: "8px", p: 1.25 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: rationale ? 0.4 : 0 }}>
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: TOKENS.text }}>{formatKey(k)}</Typography>
                    <Chip label={style.label} size="small" sx={{ background: "#fff", color: style.color, border: `1px solid ${style.color}`, fontWeight: 700, fontSize: "0.65rem", height: 20 }} />
                  </Box>
                  {rationale && (
                    <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textMuted, lineHeight: 1.4 }}>{rationale}</Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {Object.keys(regex).length > 0 && (
        <Box>
          <Typography sx={{ fontSize: "0.65rem", color: TOKENS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>Pattern Matches</Typography>
          <Stack spacing={0.75}>
            {Object.entries(regex).map(([k, v]) => (
              <Box key={k} sx={{ background: "#fff", border: `1px solid #fde68a`, borderRadius: "8px", p: 1.5 }}>
                <Typography sx={{ fontSize: "0.65rem", color: TOKENS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>{formatKey(k)}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: TOKENS.text, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis" }}>{String(v)}</Typography>
                  <Tooltip title="Copy">
                    <IconButton size="small" onClick={() => onCopy(String(v))} sx={{ color: TOKENS.textFaint, "&:hover": { color: TOKENS.primary } }}>
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

// ── Collapsible card (drag-down accordion) ───────────────────────────────
function CollapsibleCard({ open, onToggle, icon: Icon, iconColor = TOKENS.primary, iconBg = TOKENS.primarySoft, title, subtitle, badge, action, headerBg, children }) {
  return (
    <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px", overflow: "hidden" }}>
      <Box
        onClick={onToggle}
        sx={{
          p: 2.5, cursor: "pointer", userSelect: "none",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: headerBg || "#fff",
          transition: "background 0.15s",
          "&:hover": { background: headerBg ? headerBg : TOKENS.pageBg },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
          {Icon && (
            <Box sx={{ width: 36, height: 36, borderRadius: "8px", background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon sx={{ fontSize: 20, color: iconColor }} />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "1.02rem", fontWeight: 700, color: TOKENS.text, letterSpacing: "-0.01em" }}>{title}</Typography>
              {badge && (
                <Chip label={badge} size="small" sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, background: iconBg, color: iconColor, border: `1px solid ${iconColor}20` }} />
              )}
            </Box>
            {subtitle && <Typography sx={{ fontSize: "0.8rem", color: TOKENS.textMuted, mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</Typography>}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, ml: 2 }}>
          {action && <Box onClick={(e) => e.stopPropagation()}>{action}</Box>}
          <ExpandMoreIcon sx={{
            color: TOKENS.textMuted,
            transition: "transform 0.25s",
            transform: open ? "rotate(180deg)" : "rotate(0)",
          }} />
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ borderTop: `1px solid ${TOKENS.border}`, p: 3 }}>
          {children}
        </Box>
      </Collapse>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════
export default function AIVoiceCall({ token }) {
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const getHeaders = () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" });

  // Decode JWT to know if this user is admin (the Agent Settings tab is admin-only)
  const isAdmin = useMemo(() => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.role === "admin";
    } catch { return false; }
  }, [token]);

  // ── Agent Settings (admin) ───────────────────────────────────────────
  const [agentSettings, setAgentSettings] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentSaving, setAgentSaving] = useState(false);

  const fetchAgentSettings = useCallback(async () => {
    if (!isAdmin) return;
    setAgentLoading(true);
    try {
      const res = await fetch(`${API}/api/ai-voice/agent`, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load");
      setAgentSettings(data);
    } catch (err) {
      setSnackbar({ open: true, message: "Agent settings: " + err.message, severity: "error" });
    } finally {
      setAgentLoading(false);
    }
  }, [token, isAdmin]);

  useEffect(() => { if (isAdmin) fetchAgentSettings(); }, [fetchAgentSettings, isAdmin]);

  const saveAgentSettings = async () => {
    if (!agentSettings) return;
    setAgentSaving(true);
    try {
      const res = await fetch(`${API}/api/ai-voice/agent`, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify({
          ttsModel: agentSettings.ttsModel,
          llm: agentSettings.llm,
          language: agentSettings.language,
          additionalLanguages: agentSettings.additionalLanguages,
          firstMessage: agentSettings.firstMessage,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      setSnackbar({ open: true, message: "Agent settings updated", severity: "success" });
      fetchAgentSettings();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setAgentSaving(false);
    }
  };

  const toggleLanguage = (code) => {
    if (!agentSettings) return;
    const cur = Array.isArray(agentSettings.additionalLanguages) ? agentSettings.additionalLanguages : [];
    const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
    setAgentSettings({ ...agentSettings, additionalLanguages: next });
  };

  // ── Tab 1: Voice Library ─────────────────────────────────────────────
  const [clones, setClones] = useState([]);
  const [uploadName, setUploadName] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [indianVoices, setIndianVoices] = useState([]);
  const [voiceSearch, setVoiceSearch] = useState("");
  const [savingVoice, setSavingVoice] = useState(null);

  // Collapsible section state — saved + indian open by default
  const [expanded, setExpanded] = useState({
    saved: true,
    indian: true,
    global: false,
    upload: false,
  });
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const fetchClones = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ai-voice/voice-clones`, { headers: getHeaders() });
      if (res.ok) setClones(await res.json());
    } catch {}
  }, [token]);

  const fetchAvailableVoices = useCallback(async () => {
    try {
      const [globalRes, indianRes] = await Promise.all([
        fetch(`${API}/api/ai-voice/available-voices`, { headers: getHeaders() }),
        fetch(`${API}/api/ai-voice/indian-voices`, { headers: getHeaders() }),
      ]);
      if (globalRes.ok) setAvailableVoices(await globalRes.json());
      if (indianRes.ok) setIndianVoices(await indianRes.json());
    } catch {}
  }, [token]);

  useEffect(() => { fetchClones(); fetchAvailableVoices(); }, [fetchClones, fetchAvailableVoices]);

  const handleSavePrebuilt = async (voice) => {
    setSavingVoice(voice.voiceId);
    try {
      const res = await fetch(`${API}/api/ai-voice/voice-clones/use-prebuilt`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ elevenLabsVoiceId: voice.voiceId, voiceName: voice.name, description: voice.gender + (voice.accent ? `, ${voice.accent}` : "") }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setSnackbar({ open: true, message: `"${voice.name}" added to your library`, severity: "success" });
      fetchClones();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setSavingVoice(null);
    }
  };

  const handleUploadClone = async () => {
    if (!uploadName.trim() || uploadFiles.length === 0) {
      setSnackbar({ open: true, message: "Voice name and at least one audio file are required", severity: "error" });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("voiceName", uploadName.trim());
      formData.append("description", uploadDesc.trim());
      uploadFiles.forEach((f) => formData.append("audioFiles", f));
      const res = await fetch(`${API}/api/ai-voice/voice-clones`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      setSnackbar({ open: true, message: "Voice clone created successfully", severity: "success" });
      setUploadName(""); setUploadDesc(""); setUploadFiles([]);
      fetchClones();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClone = async (id) => {
    try {
      const res = await fetch(`${API}/api/ai-voice/voice-clones/${id}`, { method: "DELETE", headers: getHeaders() });
      if (!res.ok) throw new Error("Delete failed");
      setSnackbar({ open: true, message: "Voice removed from library", severity: "success" });
      fetchClones();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  // ── Tab 2: Configure Call ────────────────────────────────────────────
  const [callForm, setCallForm] = useState({
    phoneNumber: "", fromNumber: "", fromNumberId: "", voiceCloneId: "", scenarioPrompt: "",
    callType: "phone", // 'phone' | 'teams' | 'zoom'
    teamsDialIn: "", teamsMeetingId: "", teamsPasscode: "",
    zoomDialIn: "", zoomMeetingId: "", zoomPasscode: "",
  });
  const [starting, setStarting] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState(CUSTOM_SCENARIO_OPTION.id);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(false);

  const fetchPhoneNumbers = useCallback(async () => {
    setPhoneNumbersLoading(true);
    try {
      const res = await fetch(`${API}/api/ai-voice/phone-numbers`, { headers: getHeaders() });
      const data = await res.json();
      if (res.ok) setPhoneNumbers(data.numbers || []);
    } catch { /* silent — dropdown just stays empty */ }
    finally { setPhoneNumbersLoading(false); }
  }, []);

  useEffect(() => { fetchPhoneNumbers(); }, [fetchPhoneNumbers]);

  const handleScenarioSelect = (id) => {
    setSelectedScenarioId(id);
    if (id === CUSTOM_SCENARIO_OPTION.id) return;
    const scenario = SCENARIOS.find((s) => s.id === id);
    if (scenario) setCallForm((prev) => ({ ...prev, scenarioPrompt: scenario.prompt }));
  };

  const handleStartCall = async () => {
    if (!callForm.voiceCloneId || !callForm.scenarioPrompt) {
      setSnackbar({ open: true, message: "Voice and scenario are required", severity: "error" });
      return;
    }
    if (callForm.callType === "teams") {
      if (!callForm.teamsDialIn || !callForm.teamsMeetingId) {
        setSnackbar({ open: true, message: "Teams dial-in number and Meeting ID are required", severity: "error" });
        return;
      }
    } else if (callForm.callType === "zoom") {
      if (!callForm.zoomDialIn || !callForm.zoomMeetingId) {
        setSnackbar({ open: true, message: "Zoom dial-in number and Meeting ID are required", severity: "error" });
        return;
      }
    } else if (!callForm.phoneNumber) {
      setSnackbar({ open: true, message: "Phone number is required", severity: "error" });
      return;
    }
    setStarting(true);
    try {
      let body;
      if (callForm.callType === "teams") {
        body = {
          phoneNumber: callForm.teamsDialIn, // fallback label; teamsDialIn is the real destination
          fromNumber: callForm.fromNumber,
          fromNumberId: callForm.fromNumberId,
          voiceCloneId: callForm.voiceCloneId,
          scenarioPrompt: callForm.scenarioPrompt,
          teamsDialIn: callForm.teamsDialIn,
          teamsMeetingId: callForm.teamsMeetingId,
          ...(callForm.teamsPasscode ? { teamsPasscode: callForm.teamsPasscode } : {}),
        };
      } else if (callForm.callType === "zoom") {
        body = {
          phoneNumber: callForm.zoomDialIn, // fallback label; zoomDialIn is the real destination
          fromNumber: callForm.fromNumber,
          fromNumberId: callForm.fromNumberId,
          voiceCloneId: callForm.voiceCloneId,
          scenarioPrompt: callForm.scenarioPrompt,
          zoomDialIn: callForm.zoomDialIn,
          zoomMeetingId: callForm.zoomMeetingId,
          ...(callForm.zoomPasscode ? { zoomPasscode: callForm.zoomPasscode } : {}),
        };
      } else {
        body = {
          phoneNumber: callForm.phoneNumber,
          fromNumber: callForm.fromNumber,
          fromNumberId: callForm.fromNumberId,
          voiceCloneId: callForm.voiceCloneId,
          scenarioPrompt: callForm.scenarioPrompt,
        };
      }
      const res = await fetch(`${API}/api/ai-voice/calls`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to start call");
      setSnackbar({ open: true, message: `Call dispatched · ID #${data.callDbId}`, severity: "success" });
      setCallForm({ phoneNumber: "", fromNumber: "", fromNumberId: "", voiceCloneId: "", scenarioPrompt: "", callType: "phone", teamsDialIn: "", teamsMeetingId: "", teamsPasscode: "", zoomDialIn: "", zoomMeetingId: "", zoomPasscode: "" });
      setSelectedScenarioId(CUSTOM_SCENARIO_OPTION.id);
      setTab(3);
      fetchCalls();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setStarting(false);
    }
  };

  // ── Tab 3: Live Calls ────────────────────────────────────────────────
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, withData: 0 });
  const [detailCall, setDetailCall] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [callsFilter, setCallsFilter] = useState("all");
  const transcriptEndRef = useRef(null);

  // Live-poll the open detail dialog every 3 s while the call is active
  useEffect(() => {
    if (!detailOpen || !detailCall) return;
    const activeStatuses = ["initiated", "ringing", "in-progress"];
    if (!activeStatuses.includes(detailCall.callStatus)) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/ai-voice/calls/${detailCall.id}`, { headers: getHeaders() });
        if (res.ok) setDetailCall(await res.json());
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [detailOpen, detailCall?.id, detailCall?.callStatus]);

  useEffect(() => {
    if (transcriptEndRef.current) transcriptEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [detailCall?.transcript?.length]);

  const fetchCalls = useCallback(async () => {
    try {
      const [callsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/ai-voice/calls`, { headers: getHeaders() }),
        fetch(`${API}/api/ai-voice/stats`, { headers: getHeaders() }),
      ]);
      if (callsRes.ok) { const d = await callsRes.json(); setCalls(d.calls || []); }
      if (statsRes.ok) setStats(await statsRes.json());
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchCalls();
    const interval = setInterval(fetchCalls, 10000);
    return () => clearInterval(interval);
  }, [fetchCalls]);

  const openDetail = async (call) => {
    try {
      const res = await fetch(`${API}/api/ai-voice/calls/${call.id}`, { headers: getHeaders() });
      if (res.ok) { setDetailCall(await res.json()); setDetailOpen(true); }
    } catch {}
  };

  const downloadCallPdf = async (call) => {
    try {
      const res = await fetch(`${API}/api/ai-voice/calls/${call.id}`, { headers: getHeaders() });
      if (!res.ok) throw new Error("Failed to fetch call detail");
      const full = await res.json();
      generateCallPdf(full);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Could not generate PDF", severity: "error" });
    }
  };

  const filteredCalls = useMemo(() => {
    if (callsFilter === "all") return calls;
    if (callsFilter === "active") return calls.filter((c) => ["initiated", "ringing", "in-progress"].includes(c.callStatus));
    if (callsFilter === "completed") return calls.filter((c) => c.callStatus === "completed");
    if (callsFilter === "captured") return calls.filter((c) => c.extractedData && c.extractedData !== "{}");
    return calls;
  }, [calls, callsFilter]);

  const previewExtracted = (raw) => {
    try {
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      const keys = Object.keys(d || {});
      if (keys.length === 0) return null;
      return keys;
    } catch { return null; }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: "Copied to clipboard", severity: "success" });
  };

  const indianFemale = indianVoices.filter((v) => v.gender === "female");
  const indianMale = indianVoices.filter((v) => v.gender === "male");
  const filteredGlobal = availableVoices.filter((v) =>
    v.name.toLowerCase().includes(voiceSearch.toLowerCase()) ||
    (v.accent || "").toLowerCase().includes(voiceSearch.toLowerCase())
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ background: TOKENS.pageBg, minHeight: "100vh" }}>
      {/* ── Top header bar ─────────────────────────────────────────────── */}
      <Box sx={{
        background: "#fff", borderBottom: `1px solid ${TOKENS.border}`,
        px: { xs: 3, md: 5 }, py: 2.5,
      }}>
        <Box sx={{ maxWidth: 1400, mx: "auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{
              width: 42, height: 42, borderRadius: "10px",
              background: `linear-gradient(135deg, ${TOKENS.primary}, ${TOKENS.accent})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 12px rgba(79,70,229,0.25)",
            }}>
              <RecordVoiceOverIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: TOKENS.text, lineHeight: 1.1, letterSpacing: "-0.025em" }}>
                AI Voice
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: TOKENS.textMuted, mt: 0.25 }}>
                Conversational AI · Voice cloning · Live data capture
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.75, px: 1.5, py: 0.75, borderRadius: "8px", background: TOKENS.successSoft, border: `1px solid #bbf7d0` }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: TOKENS.success, animation: "pulse 1.6s ease-in-out infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.5 } } }} />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: TOKENS.success }}>System Operational</Typography>
            </Box>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 3, md: 5 }, maxWidth: 1400, mx: "auto" }}>
        {/* ── Tabs ─────────────────────────────────────────────────────── */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 0, borderBottom: `1px solid ${TOKENS.border}`, minHeight: 44,
            "& .MuiTab-root": {
              fontWeight: 600, textTransform: "none", fontSize: "0.88rem",
              color: TOKENS.textMuted, minHeight: 44, px: 2.5,
              "&.Mui-selected": { color: TOKENS.text },
            },
          }}
          TabIndicatorProps={{ style: { background: TOKENS.primary, height: 2 } }}
        >
          <Tab label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Voice Library
              <Chip label={clones.length} size="small" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, background: tab === 0 ? TOKENS.primarySoft : "#f1f5f9", color: tab === 0 ? TOKENS.primary : TOKENS.textMuted }} />
            </Box>
          } />
          <Tab label="Configure Call" />
          <Tab label="Bulk Calls" />
          <Tab label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Operations
              {stats.active > 0 && (
                <Chip label={`${stats.active} live`} size="small" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, background: TOKENS.successSoft, color: TOKENS.success }} />
              )}
            </Box>
          } />
          {isAdmin && (
            <Tab label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SettingsIcon sx={{ fontSize: 16 }} />
                Agent Settings
              </Box>
            } />
          )}
        </Tabs>

        {/* ════════════════════════════════════════════════════════════════
            TAB 1 — VOICE LIBRARY (collapsible accordions)
        ═══════════════════════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

            {/* My Voices */}
            <CollapsibleCard
              open={expanded.saved}
              onToggle={() => toggle("saved")}
              icon={StorageIcon}
              title="My Voice Library"
              subtitle="Voices saved to your account, ready for outbound calls"
              badge={`${clones.length} ${clones.length === 1 ? "voice" : "voices"}`}
              action={
                <Tooltip title="Refresh">
                  <IconButton onClick={fetchClones} size="small" sx={{ color: TOKENS.textMuted }}>
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              }
            >
              {clones.length === 0 ? (
                <EmptyState
                  icon={RecordVoiceOverIcon}
                  title="No voices in your library yet"
                  subtitle="Browse the catalog below — Indian voices are recommended for local scenarios. Selected voices appear here and become available when configuring a call."
                />
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 2 }}>
                  {clones.map((c) => <SavedVoiceCard key={c.id} clone={c} onDelete={handleDeleteClone} />)}
                </Box>
              )}
            </CollapsibleCard>

            {/* Indian voices */}
            <CollapsibleCard
              open={expanded.indian}
              onToggle={() => toggle("indian")}
              icon={AutoAwesomeIcon}
              iconColor={TOKENS.warning}
              iconBg="#fef3c7"
              title="Indian English Voices"
              subtitle="Curated voices optimised for Indian banking, telecom and corporate scenarios"
              badge="Recommended"
              headerBg="linear-gradient(135deg, #fffbeb, #fef3c7)"
            >
              {indianVoices.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={20} sx={{ color: TOKENS.warning }} />
                </Box>
              ) : (
                <Stack spacing={3}>
                  <Box>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                      Female Voices · {indianFemale.length}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 1.25 }}>
                      {indianFemale.map((v) => (
                        <VoiceRow key={v.voiceId} voice={v} alreadySaved={clones.some((c) => c.elevenLabsVoiceId === v.voiceId)} saving={savingVoice === v.voiceId} onSave={handleSavePrebuilt} />
                      ))}
                    </Box>
                  </Box>
                  <Divider sx={{ borderColor: TOKENS.border }} />
                  <Box>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                      Male Voices · {indianMale.length}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 1.25 }}>
                      {indianMale.map((v) => (
                        <VoiceRow key={v.voiceId} voice={v} alreadySaved={clones.some((c) => c.elevenLabsVoiceId === v.voiceId)} saving={savingVoice === v.voiceId} onSave={handleSavePrebuilt} />
                      ))}
                    </Box>
                  </Box>
                </Stack>
              )}
            </CollapsibleCard>

            {/* Global voice library */}
            <CollapsibleCard
              open={expanded.global}
              onToggle={() => toggle("global")}
              icon={PublicIcon}
              title="Global Voice Catalog"
              subtitle="Pre-built professional voices for diverse personas"
              badge={`${availableVoices.length} available`}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or accent..."
                value={voiceSearch}
                onChange={(e) => setVoiceSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: TOKENS.textFaint }} /></InputAdornment>,
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", background: TOKENS.pageBg, fontSize: "0.85rem" },
                }}
              />
              <Box sx={{
                display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 1.25,
                maxHeight: 360, overflowY: "auto", pr: 0.5,
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": { background: TOKENS.border, borderRadius: 3 },
              }}>
                {filteredGlobal.length === 0 ? (
                  <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 4, color: TOKENS.textFaint, fontSize: "0.85rem" }}>
                    No voices match your search
                  </Box>
                ) : (
                  filteredGlobal.map((v) => (
                    <VoiceRow key={v.voiceId} voice={v} alreadySaved={clones.some((c) => c.elevenLabsVoiceId === v.voiceId)} saving={savingVoice === v.voiceId} onSave={handleSavePrebuilt} />
                  ))
                )}
              </Box>
            </CollapsibleCard>

            {/* Upload custom clone */}
            <CollapsibleCard
              open={expanded.upload}
              onToggle={() => toggle("upload")}
              icon={CloudUploadIcon}
              title="Clone Your Own Voice"
              subtitle="Upload 1–3 minutes of your voice to create a custom AI voice"
              badge="Paid plan required"
            >
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: `2px dashed ${uploadFiles.length ? TOKENS.primary : TOKENS.border}`,
                  borderRadius: "10px", p: 3.5, mb: 2,
                  textAlign: "center", cursor: "pointer",
                  background: uploadFiles.length ? TOKENS.primarySoft : TOKENS.pageBg,
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: TOKENS.primary, background: TOKENS.primarySoft },
                }}
              >
                <CloudUploadIcon sx={{ color: TOKENS.primary, fontSize: 36, mb: 1 }} />
                <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: TOKENS.text }}>
                  {uploadFiles.length > 0 ? `${uploadFiles.length} file${uploadFiles.length > 1 ? "s" : ""} selected` : "Drop audio files or click to browse"}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: TOKENS.textMuted, mt: 0.5 }}>
                  MP3, WAV, M4A · 1–5 minutes of clear speech recommended · Max 50MB per file
                </Typography>
                <input ref={fileInputRef} type="file" multiple accept=".mp3,.wav,.m4a,.webm" style={{ display: "none" }}
                  onChange={(e) => setUploadFiles(Array.from(e.target.files))} />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}>
                <TextField
                  fullWidth size="small" label="Voice Name"
                  value={uploadName} onChange={(e) => setUploadName(e.target.value)}
                  placeholder="e.g. Priya HR Manager"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
                <TextField
                  fullWidth size="small" label="Description (optional)"
                  value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)}
                  placeholder="Female, corporate, Indian English"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Box>
              <Button
                variant="contained" onClick={handleUploadClone} disabled={uploading}
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
                sx={{
                  background: TOKENS.primary, "&:hover": { background: TOKENS.primaryDark },
                  textTransform: "none", fontWeight: 600, borderRadius: "8px",
                  px: 3, py: 1, boxShadow: "none",
                }}
              >
                {uploading ? "Creating clone..." : "Create Voice Clone"}
              </Button>
            </CollapsibleCard>

          </Box>
        </TabPanel>

        {/* ════════════════════════════════════════════════════════════════
            TAB 2 — CONFIGURE CALL
        ═══════════════════════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" }, gap: 3 }}>
            {/* Left — main form */}
            <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px" }}>
              <CardContent sx={{ p: "28px !important" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: "8px", background: TOKENS.primarySoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <PhoneIcon sx={{ fontSize: 20, color: TOKENS.primary }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: TOKENS.text, letterSpacing: "-0.01em" }}>Call Configuration</Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: TOKENS.textMuted, mt: 0.25 }}>Define the target, voice and AI persona for an outbound vishing simulation</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Target
                  </Typography>
                  {/* Call-type toggle: direct phone vs Teams meeting vs Zoom meeting */}
                  <Box sx={{ display: "inline-flex", background: TOKENS.pageBg, border: `1px solid ${TOKENS.border}`, borderRadius: "8px", p: "3px" }}>
                    {[
                      { key: "phone", label: "Phone Call" },
                      { key: "teams", label: "Teams Meeting" },
                      { key: "zoom",  label: "Zoom Meeting" },
                    ].map(({ key, label }) => {
                      const active = callForm.callType === key;
                      return (
                        <Button
                          key={key}
                          size="small"
                          onClick={() => setCallForm({ ...callForm, callType: key })}
                          sx={{
                            textTransform: "none", fontWeight: 600, fontSize: "0.74rem",
                            borderRadius: "6px", px: 1.5, py: 0.5, minWidth: "auto",
                            ...(active
                              ? { background: TOKENS.surface, color: TOKENS.text, boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }
                              : { color: TOKENS.textMuted, "&:hover": { background: "transparent" } }),
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {/* Caller ID — one dropdown shared across phone/Teams/Zoom modes */}
                <FormControl fullWidth size="small" sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
                  <InputLabel>Caller ID (from number)</InputLabel>
                  <Select
                    value={callForm.fromNumberId}
                    label="Caller ID (from number)"
                    onChange={(e) => {
                      const id = e.target.value;
                      const picked = phoneNumbers.find((n) => n.id === id);
                      setCallForm({
                        ...callForm,
                        fromNumberId: id,
                        fromNumber: picked ? picked.phoneNumber : "",
                      });
                    }}
                  >
                    <MenuItem value="">
                      <Typography sx={{ fontSize: "0.85rem", color: TOKENS.textMuted, fontWeight: 500 }}>
                        {phoneNumbersLoading ? "Loading…" : "Use default sender"}
                      </Typography>
                    </MenuItem>
                    {phoneNumbers.map((n) => (
                      <MenuItem key={n.id} value={n.id}>
                        <Box>
                          <Typography sx={{ fontSize: "0.87rem", fontWeight: 600, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                            {n.phoneNumber}
                          </Typography>
                          {n.label && (
                            <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textMuted }}>
                              {n.label}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                    {!phoneNumbersLoading && phoneNumbers.length === 0 && (
                      <MenuItem disabled value="">
                        <Typography sx={{ fontSize: "0.78rem", color: TOKENS.textFaint, fontStyle: "italic" }}>
                          No numbers found — register a Twilio number in the ElevenLabs dashboard
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                {callForm.callType === "phone" ? (
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth size="small" label="Target Phone Number"
                      value={callForm.phoneNumber}
                      onChange={(e) => setCallForm({ ...callForm, phoneNumber: e.target.value })}
                      placeholder="+919876543210"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    />
                  </Box>
                ) : callForm.callType === "teams" ? (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 1.5 }}>
                      <TextField
                        fullWidth size="small" label="Teams Dial-In Number (required)"
                        value={callForm.teamsDialIn}
                        onChange={(e) => setCallForm({ ...callForm, teamsDialIn: e.target.value })}
                        placeholder="+91 22 6294 0000"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      />
                      <TextField
                        fullWidth size="small" label="Meeting / Conference ID (required)"
                        value={callForm.teamsMeetingId}
                        onChange={(e) => setCallForm({ ...callForm, teamsMeetingId: e.target.value })}
                        placeholder="123 456 789"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 1.5 }}>
                      <TextField
                        fullWidth size="small" label="Meeting Passcode (optional)"
                        value={callForm.teamsPasscode}
                        onChange={(e) => setCallForm({ ...callForm, teamsPasscode: e.target.value })}
                        placeholder="Only if your meeting requires one"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      />
                      <Box />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", p: 1.5, background: TOKENS.warningSoft, border: `1px solid #fde68a`, borderRadius: "8px" }}>
                      <InfoIcon sx={{ fontSize: 16, color: TOKENS.warning, mt: 0.2, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.74rem", color: TOKENS.text, lineHeight: 1.5 }}>
                        <strong>Authorized testing only.</strong> Only dial into meetings your security team has set up for awareness training where participants are aware.
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 1.5 }}>
                      <TextField
                        fullWidth size="small" label="Zoom Dial-In Number (required)"
                        value={callForm.zoomDialIn}
                        onChange={(e) => setCallForm({ ...callForm, zoomDialIn: e.target.value })}
                        placeholder="+1 669 900 6833"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      />
                      <TextField
                        fullWidth size="small" label="Meeting ID (required)"
                        value={callForm.zoomMeetingId}
                        onChange={(e) => setCallForm({ ...callForm, zoomMeetingId: e.target.value })}
                        placeholder="123 456 7890"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 1.5 }}>
                      <TextField
                        fullWidth size="small" label="Meeting Passcode (optional)"
                        value={callForm.zoomPasscode}
                        onChange={(e) => setCallForm({ ...callForm, zoomPasscode: e.target.value })}
                        placeholder="Only if your meeting requires one"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      />
                      <Box />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", p: 1.5, background: TOKENS.warningSoft, border: `1px solid #fde68a`, borderRadius: "8px" }}>
                      <InfoIcon sx={{ fontSize: 16, color: TOKENS.warning, mt: 0.2, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.74rem", color: TOKENS.text, lineHeight: 1.5 }}>
                        <strong>Authorized testing only.</strong> Only dial into meetings your security team has set up for awareness training where participants are aware.
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                  Voice Persona
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
                  <InputLabel>Voice from your library</InputLabel>
                  <Select
                    value={callForm.voiceCloneId}
                    label="Voice from your library"
                    onChange={(e) => setCallForm({ ...callForm, voiceCloneId: e.target.value })}
                  >
                    {clones.length === 0 && (
                      <MenuItem disabled value="">No voices saved — add one from the Voice Library tab</MenuItem>
                    )}
                    {clones.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <VoiceAvatar name={c.voiceName} size={26} />
                          <Box>
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>{c.voiceName}</Typography>
                            {c.description && <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textMuted }}>{c.description}</Typography>}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                  Scenario Template
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
                  <InputLabel>Load from library (optional)</InputLabel>
                  <Select
                    value={selectedScenarioId}
                    label="Load from library (optional)"
                    onChange={(e) => handleScenarioSelect(e.target.value)}
                  >
                    <MenuItem value={CUSTOM_SCENARIO_OPTION.id}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: TOKENS.textMuted }}>
                        {CUSTOM_SCENARIO_OPTION.name}
                      </Typography>
                    </MenuItem>
                    {SCENARIOS.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        <Box>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>{s.name}</Typography>
                          {s.description && (
                            <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textMuted, whiteSpace: "normal", lineHeight: 1.35 }}>
                              {s.description}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                  Scenario Prompt
                  {selectedScenarioId !== CUSTOM_SCENARIO_OPTION.id && (
                    <Typography component="span" sx={{ ml: 1, fontSize: "0.68rem", fontWeight: 500, color: TOKENS.textFaint, textTransform: "none", letterSpacing: 0 }}>
                      — loaded from template, edit freely
                    </Typography>
                  )}
                </Typography>
                <TextField
                  fullWidth multiline rows={10} size="small"
                  value={callForm.scenarioPrompt}
                  onChange={(e) => setCallForm({ ...callForm, scenarioPrompt: e.target.value })}
                  placeholder="Pick a template from the dropdown above, or write your own scenario here. Example: You are Priya from HR calling about a compensation letter that needs urgent review..."
                  sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.88rem" } }}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained" size="large"
                    onClick={handleStartCall}
                    disabled={starting}
                    startIcon={starting ? <CircularProgress size={18} color="inherit" /> : <PhoneIcon />}
                    sx={{
                      background: TOKENS.primary, "&:hover": { background: TOKENS.primaryDark },
                      textTransform: "none", fontWeight: 600, borderRadius: "8px",
                      px: 3.5, py: 1.25, boxShadow: "none", fontSize: "0.92rem",
                    }}
                  >
                    {starting ? "Dispatching..." : "Dispatch Call"}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Right — sidebar with safety + flow */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px", background: TOKENS.infoSoft }}>
                <CardContent sx={{ p: "20px !important" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <ShieldIcon sx={{ fontSize: 22, color: TOKENS.info, flexShrink: 0, mt: 0.25 }} />
                    <Box>
                      <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: TOKENS.text, mb: 0.5 }}>
                        Authorised Simulations Only
                      </Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: TOKENS.textMuted, lineHeight: 1.55 }}>
                        Use only on phone numbers you own or have explicit written authorisation to test. All conversations are logged for compliance review.
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px" }}>
                <CardContent sx={{ p: "20px !important" }}>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 2 }}>
                    Call Flow
                  </Typography>
                  <Stack spacing={2}>
                    {[
                      { n: 1, t: "Greeting Pre-Generation", d: "AI drafts the opening line and synthesises it in your selected voice." },
                      { n: 2, t: "Outbound Dial", d: "Outbound call is placed from your configured number." },
                      { n: 3, t: "Live Conversation", d: "Caller speech is transcribed in real-time. AI responds in 1-2 sentences." },
                      { n: 4, t: "Data Capture", d: "Spoken account numbers, OTPs, employee IDs are auto-extracted and indexed." },
                      { n: 5, t: "Auto Termination", d: "Call ends when objective is met or caller refuses." },
                    ].map((step) => (
                      <Box key={step.n} sx={{ display: "flex", gap: 1.5 }}>
                        <Box sx={{
                          width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                          background: TOKENS.primarySoft, color: TOKENS.primary,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "0.72rem", fontWeight: 700,
                        }}>
                          {step.n}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: TOKENS.text, lineHeight: 1.3 }}>{step.t}</Typography>
                          <Typography sx={{ fontSize: "0.74rem", color: TOKENS.textMuted, lineHeight: 1.45, mt: 0.25 }}>{step.d}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </TabPanel>

        {/* ════════════════════════════════════════════════════════════════
            TAB 3 — BULK CALLS
        ═══════════════════════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={2}>
          <BulkCallPanel
            token={token}
            clones={clones}
            phoneNumbers={phoneNumbers}
            phoneNumbersLoading={phoneNumbersLoading}
            onDispatched={(count) => {
              setSnackbar({ open: true, message: `Campaign dispatched · ${count} calls queued`, severity: "success" });
              setTab(3);
              fetchCalls();
            }}
          />
        </TabPanel>

        {/* ════════════════════════════════════════════════════════════════
            TAB 4 — OPERATIONS / LIVE CALLS
        ═══════════════════════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={3}>
          {/* KPIs */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" }, gap: 2, mb: 3 }}>
            <KpiCard label="Total Calls" value={stats.total} icon={PhoneIcon} accent={TOKENS.primary} accentBg={TOKENS.primarySoft} />
            <KpiCard label="Active" value={stats.active} icon={PhoneInTalkIcon} accent={TOKENS.warning} accentBg={TOKENS.warningSoft} />
            <KpiCard label="Completed" value={stats.completed} icon={CheckCircleIcon} accent={TOKENS.success} accentBg={TOKENS.successSoft} />
            <KpiCard label="Data Captured" value={stats.withData} icon={ShieldIcon} accent={TOKENS.info} accentBg={TOKENS.infoSoft} />
          </Box>

          {/* Filter bar */}
          <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px" }}>
            <CardContent sx={{ p: "20px !important", "&:last-child": { pb: "20px !important" } }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <TuneIcon sx={{ fontSize: 18, color: TOKENS.textMuted }} />
                  {[
                    { v: "all", l: `All · ${calls.length}` },
                    { v: "active", l: `Active · ${stats.active}` },
                    { v: "completed", l: `Completed · ${stats.completed}` },
                    { v: "captured", l: `With Data · ${stats.withData}` },
                  ].map((f) => (
                    <Button
                      key={f.v}
                      size="small"
                      onClick={() => setCallsFilter(f.v)}
                      sx={{
                        fontSize: "0.76rem", fontWeight: 600, textTransform: "none",
                        borderRadius: "6px", px: 1.5, py: 0.5, minWidth: "auto",
                        ...(callsFilter === f.v
                          ? { background: TOKENS.primarySoft, color: TOKENS.primary, "&:hover": { background: TOKENS.primarySoft } }
                          : { color: TOKENS.textMuted, "&:hover": { background: TOKENS.pageBg } }),
                      }}
                    >
                      {f.l}
                    </Button>
                  ))}
                </Box>
                <IconButton onClick={fetchCalls} size="small" sx={{ color: TOKENS.textMuted }}>
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* Table */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{
                      "& th": {
                        fontWeight: 700, color: TOKENS.textMuted, fontSize: "0.7rem",
                        background: TOKENS.pageBg, borderBottom: `1px solid ${TOKENS.border}`,
                        textTransform: "uppercase", letterSpacing: "0.04em", py: 1.25,
                      },
                    }}>
                      <TableCell>Target</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Voice</TableCell>
                      <TableCell>Captured</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCalls.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                          <EmptyState
                            icon={PhoneIcon}
                            title={callsFilter === "all" ? "No calls placed yet" : "No calls match this filter"}
                            subtitle={callsFilter === "all" ? "Configure a call in the previous tab to get started." : "Adjust the filter or place a new call."}
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCalls.map((call) => {
                        const captured = previewExtracted(call.extractedData);
                        return (
                          <TableRow key={call.id} sx={{ "&:hover": { background: TOKENS.pageBg }, transition: "background 0.1s" }}>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: TOKENS.text, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                                {call.phoneNumber}
                              </Typography>
                              <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textFaint }}>#{call.id}</Typography>
                            </TableCell>
                            <TableCell><StatusChip status={call.callStatus} /></TableCell>
                            <TableCell sx={{ fontSize: "0.82rem", color: TOKENS.textMuted }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <VoiceAvatar name={call.voiceName} size={24} />
                                {call.voiceName || "—"}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {captured && captured.length > 0 ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {captured.slice(0, 3).map((k) => (
                                    <Chip key={k} label={k} size="small" sx={{ height: 20, fontSize: "0.66rem", fontWeight: 600, background: TOKENS.successSoft, color: TOKENS.success, border: `1px solid #bbf7d0` }} />
                                  ))}
                                  {captured.length > 3 && <Chip label={`+${captured.length - 3}`} size="small" sx={{ height: 20, fontSize: "0.66rem", background: "#f1f5f9" }} />}
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: "0.78rem", color: TOKENS.textFaint }}>—</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.78rem", color: TOKENS.textMuted }}>
                              {new Date(call.startTime).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Download PDF">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => downloadCallPdf(call)}
                                    sx={{ color: TOKENS.textMuted, "&:hover": { color: TOKENS.primary, background: TOKENS.primarySoft } }}
                                  >
                                    <DownloadIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="View transcript & data">
                                <IconButton size="small" onClick={() => openDetail(call)} sx={{ color: TOKENS.textMuted, "&:hover": { color: TOKENS.primary, background: TOKENS.primarySoft } }}>
                                  <InfoIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </TabPanel>

        {/* ════════════════════════════════════════════════════════════════
            TAB 4 — AGENT SETTINGS (admin only)
        ═══════════════════════════════════════════════════════════════════ */}
        {isAdmin && (
          <TabPanel value={tab} index={4}>
            {agentLoading && !agentSettings ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: TOKENS.primary }} />
              </Box>
            ) : !agentSettings ? (
              <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px" }}>
                <CardContent sx={{ p: "24px !important" }}>
                  <Typography sx={{ color: TOKENS.textMuted }}>
                    Could not load agent settings. Please contact your administrator.
                  </Typography>
                  <Button onClick={fetchAgentSettings} startIcon={<RefreshIcon />} sx={{ mt: 2, textTransform: "none" }}>Retry</Button>
                </CardContent>
              </Card>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, maxWidth: 720 }}>

                {/* Info banner */}
                <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px", background: TOKENS.infoSoft }}>
                  <CardContent sx={{ p: "16px 20px !important" }}>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <InfoIcon sx={{ fontSize: 20, color: TOKENS.info, mt: 0.25, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.85rem", color: TOKENS.text, lineHeight: 1.55 }}>
                        These settings affect <strong>every AI voice call</strong> across your workspace.
                        Per-call scenario prompt and voice selection still come from the call form.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* TTS Model */}
                <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px" }}>
                  <CardContent sx={{ p: "20px 24px !important" }}>
                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: TOKENS.text, mb: 0.5 }}>
                      Voice Synthesis Model
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: TOKENS.textMuted, mb: 2 }}>
                      The TTS model that generates the agent's speech.
                      Use a <strong>multilingual</strong> model only if your primary language is non-English or you have additional languages.
                      Use an <strong>English-only</strong> model when the agent is English-only.
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
                      <InputLabel>TTS Model</InputLabel>
                      <Select
                        value={agentSettings.ttsModel || ""}
                        label="TTS Model"
                        onChange={(e) => setAgentSettings({ ...agentSettings, ttsModel: e.target.value })}
                      >
                        <MenuItem disabled sx={{ opacity: "0.6 !important", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>Multilingual (for Hindi / Hinglish / other languages)</MenuItem>
                        <MenuItem value="eleven_turbo_v2_5">Turbo v2.5 · Multilingual · Fast (recommended for Hinglish)</MenuItem>
                        <MenuItem value="eleven_multilingual_v2">Multilingual v2 · Highest quality, slower</MenuItem>
                        <MenuItem value="eleven_flash_v2_5">Flash v2.5 · Multilingual · Fastest</MenuItem>

                        <MenuItem disabled sx={{ opacity: "0.6 !important", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", mt: 1 }}>English-only</MenuItem>
                        <MenuItem value="eleven_turbo_v2">Turbo v2 · English-only · Fast</MenuItem>
                        <MenuItem value="eleven_flash_v2">Flash v2 · English-only · Fastest</MenuItem>

                        <MenuItem disabled sx={{ opacity: "0.6 !important", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", mt: 1 }}>Newest (higher latency)</MenuItem>
                        <MenuItem value="eleven_v3">v3 Conversational · Most expressive, slower</MenuItem>

                        {/* Fallback so the agent's actual model is visible even if not in the list above */}
                        {agentSettings.ttsModel && !["eleven_turbo_v2_5","eleven_multilingual_v2","eleven_flash_v2_5","eleven_turbo_v2","eleven_flash_v2","eleven_v3"].includes(agentSettings.ttsModel) && (
                          <MenuItem value={agentSettings.ttsModel}>{agentSettings.ttsModel} (current)</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: "0.72rem", color: TOKENS.textFaint, mt: 1.5, lineHeight: 1.5 }}>
                      <strong>Tip:</strong> If you get "English Agents must use turbo or flash v2", either pick an English-only model above,
                      or change your <strong>Primary Language</strong> below to Hindi (or add Hindi as additional) to enable the multilingual models.
                    </Typography>
                  </CardContent>
                </Card>

                {/* LLM */}
                <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px" }}>
                  <CardContent sx={{ p: "20px 24px !important" }}>
                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: TOKENS.text, mb: 0.5 }}>
                      AI Brain (LLM)
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: TOKENS.textMuted, mb: 2 }}>
                      Decides what the agent says each turn. Smaller/faster models give lower latency.
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
                      <InputLabel>LLM</InputLabel>
                      <Select
                        value={agentSettings.llm || ""}
                        label="LLM"
                        onChange={(e) => setAgentSettings({ ...agentSettings, llm: e.target.value })}
                      >
                        <MenuItem value="claude-3-5-haiku">Claude Haiku · Fast, conversational</MenuItem>
                        <MenuItem value="claude-3-5-sonnet">Claude Sonnet · Smarter, slower</MenuItem>
                        <MenuItem value="gpt-4o-mini">GPT-4o Mini · Fast</MenuItem>
                        <MenuItem value="gpt-4o">GPT-4o · Smarter, slower</MenuItem>
                        <MenuItem value="gemini-2.0-flash-001">Gemini 2.0 Flash · Fast</MenuItem>
                        {/* Show current value if not in the list */}
                        {agentSettings.llm && !["claude-3-5-haiku","claude-3-5-sonnet","gpt-4o-mini","gpt-4o","gemini-2.0-flash-001"].includes(agentSettings.llm) && (
                          <MenuItem value={agentSettings.llm}>{agentSettings.llm} (current)</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>

                {/* Languages */}
                <Card sx={{ border: `1px solid ${TOKENS.border}`, boxShadow: "none", borderRadius: "12px" }}>
                  <CardContent sx={{ p: "20px 24px !important" }}>
                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: TOKENS.text, mb: 0.5 }}>
                      Languages
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: TOKENS.textMuted, mb: 2 }}>
                      Set the agent's primary language. Add more so it can switch (e.g. Hindi + English for Hinglish).
                    </Typography>

                    <FormControl fullWidth size="small" sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
                      <InputLabel>Primary Language</InputLabel>
                      <Select
                        value={agentSettings.language || ""}
                        label="Primary Language"
                        onChange={(e) => setAgentSettings({ ...agentSettings, language: e.target.value })}
                      >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="hi">Hindi</MenuItem>
                        <MenuItem value="es">Spanish</MenuItem>
                        <MenuItem value="fr">French</MenuItem>
                        <MenuItem value="de">German</MenuItem>
                        <MenuItem value="ja">Japanese</MenuItem>
                        <MenuItem value="ar">Arabic</MenuItem>
                      </Select>
                    </FormControl>

                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                      Additional Languages
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {[
                        { code: "en", label: "English" },
                        { code: "hi", label: "Hindi" },
                        { code: "bn", label: "Bengali" },
                        { code: "ta", label: "Tamil" },
                        { code: "te", label: "Telugu" },
                        { code: "mr", label: "Marathi" },
                        { code: "gu", label: "Gujarati" },
                        { code: "es", label: "Spanish" },
                        { code: "fr", label: "French" },
                        { code: "de", label: "German" },
                        { code: "ja", label: "Japanese" },
                        { code: "ar", label: "Arabic" },
                      ].map(({ code, label }) => {
                        const isPrimary = agentSettings.language === code;
                        const isActive = (agentSettings.additionalLanguages || []).includes(code);
                        return (
                          <Chip
                            key={code}
                            label={label}
                            clickable={!isPrimary}
                            disabled={isPrimary}
                            onClick={() => !isPrimary && toggleLanguage(code)}
                            sx={{
                              height: 28,
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              borderRadius: "6px",
                              ...(isPrimary
                                ? { background: TOKENS.primarySoft, color: TOKENS.primary, border: `1px solid ${TOKENS.primary}55` }
                                : isActive
                                  ? { background: TOKENS.primary, color: "#fff", "&:hover": { background: TOKENS.primaryDark } }
                                  : { background: "transparent", color: TOKENS.textMuted, border: `1px solid ${TOKENS.border}`, "&:hover": { borderColor: TOKENS.borderHover } }),
                            }}
                          />
                        );
                      })}
                    </Box>
                    <Typography sx={{ fontSize: "0.72rem", color: TOKENS.textFaint, mt: 1.5 }}>
                      The primary language is shown highlighted and can't be toggled here.
                    </Typography>
                  </CardContent>
                </Card>

                {/* Save bar */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                  <Typography sx={{ fontSize: "0.78rem", color: TOKENS.textMuted }}>
                    Changes apply to all future calls immediately.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={saveAgentSettings}
                    disabled={agentSaving}
                    startIcon={agentSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{
                      background: TOKENS.primary,
                      "&:hover": { background: TOKENS.primaryDark },
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      px: 3,
                      py: 1,
                      boxShadow: "none",
                    }}
                  >
                    {agentSaving ? "Saving..." : "Save Changes"}
                  </Button>
                </Box>
              </Box>
            )}
          </TabPanel>
        )}
      </Box>

      {/* ── Detail Dialog ──────────────────────────────────────────────── */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${TOKENS.border}`, p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: TOKENS.text, letterSpacing: "-0.01em" }}>
                  Call Detail
                </Typography>
                <StatusChip status={detailCall?.callStatus} />
                {["initiated", "ringing", "in-progress"].includes(detailCall?.callStatus) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.4, background: TOKENS.dangerSoft, borderRadius: "6px", border: `1px solid #fecaca` }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.danger, animation: "pulse 1.4s ease-in-out infinite", "@keyframes pulse": { "0%,100%": { opacity: 1, transform: "scale(1)" }, "50%": { opacity: 0.5, transform: "scale(1.4)" } } }} />
                    <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: TOKENS.danger, letterSpacing: "0.06em" }}>LIVE</Typography>
                  </Box>
                )}
              </Box>
              <Typography sx={{ fontSize: "0.82rem", color: TOKENS.textMuted, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                {detailCall?.phoneNumber} · #{detailCall?.id}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {detailCall && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 320px" }, minHeight: 480 }}>
              {/* Transcript */}
              <Box sx={{ p: 3, borderRight: { xs: 0, md: `1px solid ${TOKENS.border}` }, display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 2 }}>
                  Conversation Transcript
                </Typography>
                <Box sx={{
                  flex: 1, maxHeight: 480, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.5,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-thumb": { background: TOKENS.border, borderRadius: 3 },
                }}>
                  {(detailCall.transcript || []).length === 0 ? (
                    <EmptyState icon={RecordVoiceOverIcon} title="Awaiting conversation" subtitle="Lines will appear here as the caller speaks and the AI responds." />
                  ) : (
                    (detailCall.transcript || []).map((turn, i) => (
                      <Box key={i} sx={{ display: "flex", justifyContent: turn.role === "ai" ? "flex-end" : "flex-start" }}>
                        <Box sx={{
                          maxWidth: "78%",
                          background: turn.role === "ai" ? TOKENS.primary : "#f1f5f9",
                          color: turn.role === "ai" ? "#fff" : TOKENS.text,
                          px: 2, py: 1.25,
                          borderRadius: turn.role === "ai" ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                        }}>
                          <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, opacity: 0.7, mb: 0.5, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {turn.role === "ai" ? "AI Agent" : "Target"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.86rem", lineHeight: 1.5 }}>{turn.content}</Typography>
                          {turn.time && (
                            <Typography sx={{ fontSize: "0.65rem", opacity: 0.7, mt: 0.5 }}>
                              {new Date(turn.time).toLocaleTimeString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))
                  )}
                  <div ref={transcriptEndRef} />
                </Box>
              </Box>

              {/* Sidebar — metadata & extracted data */}
              <Box sx={{ p: 3, background: TOKENS.pageBg }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                      Metadata
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textFaint, mb: 0.25 }}>Voice</Typography>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: TOKENS.text }}>{detailCall.voiceName || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textFaint, mb: 0.25 }}>Started</Typography>
                        <Typography sx={{ fontSize: "0.82rem", color: TOKENS.text }}>{new Date(detailCall.startTime).toLocaleString()}</Typography>
                      </Box>
                      {detailCall.endTime && (
                        <Box>
                          <Typography sx={{ fontSize: "0.7rem", color: TOKENS.textFaint, mb: 0.25 }}>Ended</Typography>
                          <Typography sx={{ fontSize: "0.82rem", color: TOKENS.text }}>{new Date(detailCall.endTime).toLocaleString()}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  <Divider sx={{ borderColor: TOKENS.border }} />

                  <Box>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: TOKENS.success, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}>
                      <ShieldIcon sx={{ fontSize: 14 }} />
                      Captured Data
                    </Typography>
                    <CapturedDataView data={detailCall.extractedData} onCopy={copyToClipboard} />
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${TOKENS.border}`, display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={() => detailCall && generateCallPdf(detailCall)}
            disabled={!detailCall || !(detailCall.transcript && detailCall.transcript.length > 0)}
            startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
            variant="outlined"
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: "8px",
              borderColor: TOKENS.border, color: TOKENS.text,
              "&:hover": { borderColor: TOKENS.primary, background: TOKENS.primarySoft, color: TOKENS.primary },
            }}
          >
            Download PDF
          </Button>
          <Button onClick={() => setDetailOpen(false)} sx={{ textTransform: "none", color: TOKENS.text, fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ fontWeight: 600, borderRadius: "8px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
